import React, { useEffect, useMemo, useRef, useState } from "react";
import apiRoutes from "../../api/apiRoutes";
import { apiFetchJson } from "../../utils/http";

const PAGE_SIZE = 1000;

export default function ClienteMultiSelect({
  value = [],
  onChange = () => {},
  disabled = false,
  estadoId = null,
  grupoId = null,
}) {
  const ref = useRef(null);

  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function cargarClientes() {
      setLoading(true);

      try {
        let page = 0;
        let hasMore = true;
        const acc = [];

        while (hasMore) {
          const url = apiRoutes.clientes.autocompleteUrl({
            q: "",
            page,
            size: PAGE_SIZE,
            tipo: "cliente",
            browse: true,
            grupoId,
            estadoId,
          });

          const data = await apiFetchJson(url);
          const rows = data?.items ?? [];

          acc.push(
            ...rows.map((r) => ({
              id: r.id,
              label:
                r.label ||
                r.nombre ||
                r.value ||
                r.descripcion ||
                `${r.codigo ? `${r.codigo} - ` : ""}${r.nombre ?? ""}`,
              codigo: r.codigo,
              nombre: r.nombre,
            }))
          );

          hasMore = Boolean(data?.hasMore);
          page++;
        }

        if (!cancelled) {
          setItems(acc);
        }
      } catch (e) {
        console.error("Clientes:", e);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    cargarClientes();

    return () => {
      cancelled = true;
    };
  }, [estadoId, grupoId]);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onDoc);

    return () => {
      document.removeEventListener("mousedown", onDoc);
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();

    if (!q) return items;

    return items.filter((i) =>
      `${i.id ?? ""} ${i.codigo ?? ""} ${i.nombre ?? ""} ${i.label ?? ""}`
        .toLowerCase()
        .includes(q)
    );
  }, [items, search]);

  const label =
    value.length === 0
      ? "Seleccionar clientes"
      : value.length === 1
      ? "1 cliente seleccionado"
      : `${value.length} clientes seleccionados`;

  const toggle = (id) => {
    onChange(
      value.includes(id)
        ? value.filter((x) => x !== id)
        : [...value, id]
    );
  };

  const marcarTodas = () => {
    const idsFiltrados = filtered.map((f) => f.id);

    const merged = Array.from(new Set([...value, ...idsFiltrados]));

    onChange(merged);
  };

  const desmarcarTodas = () => {
    const idsFiltrados = new Set(filtered.map((f) => f.id));

    onChange(value.filter((id) => !idsFiltrados.has(id)));
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        className="input w-full flex items-center text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="truncate">
          {loading ? "Cargando clientes..." : label}
        </span>
        <span className="ml-auto">▾</span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full border rounded bg-white shadow-xl">
          <div className="p-2 border-b bg-gray-50 flex flex-wrap gap-2">
            <input
              placeholder="Filtrar cliente"
              className="flex-1 border rounded px-2 py-1"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={loading}
            />

            <button
              type="button"
              onClick={marcarTodas}
              className="text-green-700 text-sm"
              disabled={loading || filtered.length === 0}
            >
              ✓ Marcar todas
            </button>

            <button
              type="button"
              onClick={desmarcarTodas}
              className="text-green-700 text-sm"
              disabled={loading || filtered.length === 0}
            >
              ✗ Desmarcar todas
            </button>
          </div>

          <div className="max-h-56 overflow-auto">
            {loading && (
              <div className="px-3 py-2 text-gray-500">
                Cargando...
              </div>
            )}

            {!loading && filtered.length === 0 && (
              <div className="px-3 py-2 text-gray-500">
                No se encontraron clientes
              </div>
            )}

            {!loading &&
              filtered.map((op) => (
                <label
                  key={op.id}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={value.includes(op.id)}
                    onChange={() => toggle(op.id)}
                  />
                  <span className="truncate">{op.label}</span>
                </label>
              ))}
          </div>

          <div className="p-2 border-t text-right bg-white">
            <button
              type="button"
              className="text-green-700"
              onClick={() => setOpen(false)}
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}